import React, { useCallback, useEffect, useState } from "react"
import useRetrieveData from 'global/hooks/useRetrieveData';
import { CenterLoader } from "global/styles";
import { get, isEqual, orderBy, set, size } from "global/lib/lodash";
import { Button } from 'global/components/Button';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Icon, Switch,
} from "@mui/material";
import colors from "global/styles/colors";
import sendData from "redux/actions/sendData";
import SnackAlerts from 'global/components/snack-alerts';
import { tradeStyles } from "./styles";

export const TradesList = props => {
  const classes = tradeStyles();
  const [alerts, setAlerts] = useState();
  const [state, setState] = useState({
    isEdit: false,
    data: [],
    saving: false,
    sort: {
      name: 'asc',
      customName: 'asc',
      clientKey: 'asc',
    }
  })

  const sortData = useCallback(d => {
    const dd = orderBy(d, 'defaultName', get(state, 'sort.name'));
    return dd;
  }, [state.sort]);

  const trades = useRetrieveData({ url: '/trade-types' }, {
    version: 'v2',
    build: ['tradeTypes'],
  })

  useEffect(() => {
    setState(s => ({ ...s, data: sortData(trades.data) }));
  }, [trades.data]);

  useEffect(() => {
    if (!state.isEdit && size(trades.data)) {
      setState(s => ({ ...s, data: sortData([...trades.data]) }));
    }
  }, [state.isEdit])

  const editRow = (r, i, update = true) => {
    set(state.data, i, r);
    if (update) {
      setState(s => ({ ...s, data: [...state.data] }));
    }
  };

  const save = async () => {
    try {
      setState(s => ({ ...s, saving: true }));
      const diffs = state.data.filter((d, i) => {
        const comp = trades.data[i];
        return !isEqual(d, comp)
      });

      await Promise.all(diffs.map(async d => {
        await sendData({
          url: `/trade-types/${d.id}`,
          method: 'patch',
          data: {
            data: {
              id: d.id,
              type: 'trade-types',
              attributes: {
                name: get(d, 'name'),
                enabled: !!get(d, 'enabled'),
                client_key: get(d, 'clientKey'),
              }
            }
          }
        }, { version: 'v2' })
      }))

      trades.update();
      setState(s => ({ ...s, isEdit: false }));
    } catch (err) {
      console.error(err);
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setState(s => ({ ...s, saving: false }));
    }
  }

  const toggleSort = (field, dataKey) => {
    const dir = get(state, `sort.${field}`) === 'asc' ? 'desc' : 'asc';
    const ord = orderBy(state.data, dataKey, dir);
    setState(s => ({ ...s, data: ord, sort: { ...s.sort, [field]: dir } }));
  };

  const COLUMNS = [
    {
      field: 'enabled',
      title: 'Enabled',
      renderCell: (c, i) => {
        const checked = !!get(c, 'enabled');
        const p = state.isEdit ? {
          defaultChecked: checked,
          onChange: e => editRow({ ...c, enabled: e.target.checked }, i, true),
        } : { checked };
        return <Switch size="small" color="primary" {...p} />;
      }
    },
    {
      field: 'name',
      title: 'Name',
      disableStyle: true,
      sortKey: 'name',
      dataKey: 'defaultName',
      renderCell: (c) => get(c, 'defaultName'),
    },
    {
      field: 'customName',
      title: 'Custom Name',
      disableStyle: true,
      sortKey: 'customName',
      dataKey: 'name',
      renderCell: (c, i) => {
        const val = get(c, 'name');
        if (state.isEdit) {
          const disabled = !get(c, 'enabled');
          return <TextField disabled={disabled} size="small" variant="outlined" defaultValue={val} onChange={e => editRow({ ...c, name: e.target.value }, i)} />;
        }
        return val;
      }
    },
    {
      field: 'clientKey',
      title: 'Key ID',
      disableStyle: true,
      sortKey: 'clientKey',
      dataKey: 'clientKey',
      renderCell: (c, i) => {
        const val = get(c, 'clientKey');
        if (state.isEdit) {
          const disabled = !get(c, 'enabled');
          return <TextField disabled={disabled} size="small" variant="outlined" defaultValue={val} onChange={e => editRow({ ...c, clientKey: e.target.value }, i)} />;
        }
        return val;
      }
    },
  ];

  if (trades.loading && !state.data.length) return <CenterLoader />;

  return (
    <div>
      <div style={{ padding: '16px 0', display: 'flex', alignItems: 'center' }}>
        <Button icon={state.isEdit ? "close" : "edit"} bgColor={state.isEdit ? colors.danger : undefined} title="EDIT LIST" onClick={() => setState(s => ({ ...s, isEdit: !s.isEdit }))} />
        {state.isEdit && <Button title="SAVE" icon="save" onClick={save} style={{ marginLeft: 8 }} />}
      </div>

      <div className={classes.container}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.field}
                    className={classes.headerCell}
                    onClick={col.sortKey ? () => toggleSort(col.sortKey, col.dataKey) : undefined}
                    style={col.sortKey ? { cursor: 'pointer' } : undefined}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      {col.title}
                      {col.sortKey && (
                        <Icon fontSize="small">
                          {get(state, `sort.${col.sortKey}`) === 'desc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                      )}
                    </span>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!state.saving && state.data.map((trade, i) => {
                if (!state.isEdit && !get(trade, 'enabled')) return null;
                const disabled = !get(trade, 'enabled');
                return (
                  <TableRow key={`${!!state.isEdit}-${trade.id}`} className={disabled ? classes.disabledRow : classes.enabledRow}>
                    {COLUMNS.map((col) => (
                      <TableCell
                        key={col.field}
                        className={disabled && col.disableStyle ? classes.disabledText : ''}
                      >
                        {col.renderCell(trade, i)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </div>
  )
}
